import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import SubHead from '../SubHead/SubHead';
import BodyText from '../BodyText/BodyText';
import Button from '../../../common/Button';
import { getFileName, isValid, isText, decodeSpecialCharacters, segmentTrackEvent } from '../../../../utils';
import StarRating from './StarRating';
import './image-module.scss';
import { PinterestShareButton } from 'react-share';

const ContentModule = ({
  data, className, isMobile, additionalClassName, fullUrl=null, slug, seo=null, articleLayout, isAffiliate,
}) => {
  if (!isValid(data)) { return null; }
  const {
    articleId,
    articleUrl=null,
    articleText=null,
    titleOverride,
    buttonTextOverride,
    urlOverride,
    sponsored,
    textPlacement,
    images={},
    author,
    category,
    bodyText,
    ratings_average=0,
    numOfRatings,
    photoCredit,
    photoCreditUrl,
    photoCreditOverride,
    photoCreditUrlOverride,
    pinterestImageOverride,
    tags,
  } = data;
  let {
    pinterestHide = false,
  } = data;

  const isStandard = (articleLayout === 'Standard');

  // topcard and topcardMobile keys are nested in the images array within data, this is how we access them
  const { topcard, topcardMobile, imageAlt } = images;
  // conditional logic below (lines 44-71) determines which data to display from what's in the REST object
  const imageSrc = (isMobile && isValid(topcardMobile)) ? topcardMobile : isValid(topcard) ? topcard : null;
  const renderedImage = (imageSrc !== null) ? <img className={className} src={imageSrc} alt={imageAlt ? imageAlt : getFileName(imageSrc)} loading="lazy"/> : null;
  const articleLayoutClass = isText(articleLayout) ? articleLayout.toLowerCase() : null;
  const pinMedia = isValid(pinterestImageOverride) ? pinterestImageOverride : imageSrc;
  const showPin = isValid(fullUrl) && pinMedia && isValid(seo) && isValid(seo.metaDescription);

  let placementClass = 'below';
  if ((isValid(textPlacement)) && textPlacement !== 'below') {
    placementClass = `side-${textPlacement}`;
  }
  
  const link = isValid(urlOverride) ? urlOverride : articleUrl;
  const headline = isValid(titleOverride) ? titleOverride : articleText;
  const imageUrl = link;
  const buttonWrapperClass = (placementClass === 'side-right' || placementClass === 'side-left') ? 'large-content-btn-wrapper side-btn' : 'large-content-btn-wrapper';
  const buttonText = (isText(buttonTextOverride)) ? buttonTextOverride : 'Read More';
  const { buttonUrl } = data;
  // logic for the Read More buttons on large-content-containers
  const button = isValid(buttonUrl) ? { text: buttonText, url: buttonUrl } : isValid(link) ? { text: buttonText, url: link } : null;
  const renderedButton = (button !== null) ? <Button button={button} isStandard={isStandard} /> : null;
  
  // Check if category is Recipes (we only want to show the rating if that's true)
  let isRecipe = true;
  if (isValid(category) && category !== 'recipes') {
    isRecipe = false;
  }

  // Check if ratings_average exists in REST object && is its value > 0?.
  let hasRating = true;
  if (!isValid(numOfRatings) || numOfRatings === 0) {
    hasRating = false;
  }
  const roundedRating = Math.round(ratings_average);
  const avgRating = (ratings_average && ratings_average > 0) ? roundedRating : 0;

  // Photo credit and credit URL:
  const credit = isValid(photoCreditOverride) ? photoCreditOverride : (isValid(photoCredit) && (photoCredit.toLowerCase() === "no credit")) ? "" : isValid(photoCredit) ? photoCredit : null;
  const creditUrl = isValid(photoCreditUrlOverride) ? photoCreditUrlOverride : isValid(photoCreditUrl) ? photoCreditUrl : null;

  // dynamically generates the container className
  const containerClassName = isValid(additionalClassName) ? `${className}-container ${additionalClassName} ${placementClass} ${articleLayoutClass}` : `${className}-container ${placementClass} ${articleLayoutClass}`;

  if ((!renderedImage || !showPin) || textPlacement !== 'below') {
    pinterestHide = true;
  }

  const productRel = isValid(imageUrl) ? 'noopener sponsored' : 'noopener nofollow';

  const subHeadClassName = isStandard ? 'sub-head standard-article' : 'sub-head';

  return (
    <div className={containerClassName}>
      {(isText(textPlacement) && textPlacement !== 'below') &&
        <>
          {/* IMAGE WRAPPER: image wrapper, linked/unlinked image, sponsored label, pinterest pin (if applicable) and image credit caption */}
          <div className="image-wrapper">
            {/* If an imageUrl exists, render image that is clickable/linked, etc. */}
            {imageUrl ? (
              <a className="large-product-link" href={imageUrl} target="_blank" rel={productRel} data-link-type="image-link" >
                <div className={`img-container-${className}`}>
                  {renderedImage}
                  { !pinterestHide &&
                    <PinterestShareButton
                      className="social-share-unit pinterest"
                      onClick={() => segmentTrackEvent('pinterest_share', {action: 'social', label: 'pin_share'})}
                      url={fullUrl}
                      alt={imageAlt || 'pinterest share image'}
                      description={seo.metaDescription}
                      media={pinMedia || 'https://www.purewow.com/static-gmg/purewow/logos/purewow-mark-salmon.jpg'}>
                    </PinterestShareButton>
                  }
                  <div className="overlay-img-credit-container ">
                    {creditUrl ? (
                      <a href={decodeSpecialCharacters(creditUrl)} className="img-credit" target="_blank" rel="nofollow noopener" data-link-type="credit-link" dangerouslySetInnerHTML={ { __html: credit } }></a>
                    ) : (
                      <span className="img-credit" dangerouslySetInnerHTML={ { __html: credit } }></span>
                    )}
                    {(textPlacement === 'below') &&
                    <>
                      {sponsored && <span className="sponsored-txt">Sponsored</span>}
                    </>
                    }
                  </div>
                </div>
              </a>
            ) : (
              // Else just render image, etc.
              <div className={`img-container-${className}`}>
                {renderedImage}
                { !pinterestHide &&
                  <PinterestShareButton
                    className="social-share-unit pinterest"
                    onClick={() => segmentTrackEvent('pinterest_share', {action: 'social', label: 'pin_share'})}
                    url={fullUrl}
                    alt={imageAlt || 'pinterest share image'}
                    description={seo.metaDescription}
                    media={pinMedia || 'https://www.purewow.com/static-gmg/purewow/logos/purewow-mark-salmon.jpg'}>
                  </PinterestShareButton>
                }
                {creditUrl ? (
                  <a href={decodeSpecialCharacters(creditUrl)} className="img-credit" target="_blank" rel="noopener nofollow" data-link-type="credit-link" dangerouslySetInnerHTML={ { __html: credit } }></a>
                ) : (
                  <span className="img-credit" dangerouslySetInnerHTML={ { __html: credit } }></span>
                )}
                {(textPlacement === 'below') &&
                <>
                  {sponsored && <span className="sponsored-txt">Sponsored</span>}
                </>
                }
              </div>
            )}
          </div>

          {/* IMAGE TEXT WRAPPER: generates the category/author, header (linked), horizontal rule, body text, sponsored, and rating */}
          <div className="image-text-wrapper">
            {(placementClass !== 'below') &&
              <>
                {/* Sponsored article vs not sponsored / Article picker used vs non-PW article */}
                {(!sponsored && (headline === articleText || headline === titleOverride)) && 
                  <h3 className="category-text">{category} • by {author}</h3>
                }
                {(sponsored && headline === articleText) && 
                  <h3 className="category-text">{category} • SPONSORED</h3>
                }
                {(sponsored && headline === titleOverride) && 
                  <h3 className="category-text">SPONSORED</h3>
                }
              </>
            }
            {/* If imageUrl exists, make headline linked */}
            {imageUrl ? (
              <a className="large-content-link" href={imageUrl} target="_blank" rel="noopener nofollow" data-link-type="text-link" >
                <SubHead className="sub-head" data={headline}/>
              </a>
            ) : (
                  <SubHead className="sub-head" data={headline}/>
            )}
            <hr className="rule"></hr>
            <BodyText className="body-text" data={bodyText} isAffiliate={isAffiliate} articleLayout={articleLayout}/>
            {/* Only display rating stars if viewing on desktop and category=recipes */}
            {(!isMobile && isRecipe && hasRating) && 
              <StarRating id={articleId} slug={slug} average={avgRating} isMobile={isMobile} header={false} />
            }
            {/* NOT USING - Editors will add buttons in body-text module in WP editor - Button for large-content-container */}
            <div className={buttonWrapperClass}>
              <div className="button-wrapper">{renderedButton}</div>
            </div>
          </div>
        </>
      }

      {(isText(textPlacement) && textPlacement === 'below') &&
        <>
          {/* IMAGE WRAPPER: image wrapper, linked/unlinked image, sponsored label, pinterest pin (if applicable) and image credit caption */}
          <div className="image-wrapper">
            {/* If an imageUrl exists, render image that is clickable/linked, etc. */}
            {imageUrl ? (
              <a className="large-product-link" href={imageUrl} target="_blank" rel={productRel} data-link-type="image-link" >
                <div className={`img-container-${className}`}>
                  {renderedImage}
                  { !pinterestHide &&
                    <PinterestShareButton
                      className="social-share-unit pinterest"
                      onClick={() => segmentTrackEvent('pinterest_share', {action: 'social', label: 'pin_share'})}
                      url={fullUrl}
                      alt={imageAlt || 'pinterest share image'}
                      description={seo.metaDescription}
                      media={pinMedia || 'https://www.purewow.com/static-gmg/purewow/logos/purewow-mark-salmon.jpg'}>
                    </PinterestShareButton>
                  }
                  <div className="img-credit-container">
                    {creditUrl ? (
                      <a href={decodeSpecialCharacters(creditUrl)} className="img-credit" target="_blank" rel="noopener nofollow" data-link-type="credit-link" dangerouslySetInnerHTML={ { __html: credit } }></a>
                    ) : (
                      <span className="img-credit-below">{credit}</span>
                    )}
                    {sponsored && <span className="sponsored-txt">Sponsored</span>}
                  </div>
                </div>
              </a>
            ) : (
              // Else just render image, etc.
              <div className={`img-container-${className}`}>
                {renderedImage}
                { !pinterestHide &&
                  <PinterestShareButton
                    className="social-share-unit pinterest"
                    onClick={() => segmentTrackEvent('pinterest_share', {action: 'social', label: 'pin_share'})}
                    url={fullUrl}
                    alt={imageAlt || 'pinterest share image'}
                    description={seo.metaDescription}
                    media={pinMedia || 'https://www.purewow.com/static-gmg/purewow/logos/purewow-mark-salmon.jpg'}>
                  </PinterestShareButton>
                }
                {creditUrl ? (
                  <a href={decodeSpecialCharacters(creditUrl)} className="img-credit" target="_blank" rel="noopener nofollow" data-link-type="credit-link" dangerouslySetInnerHTML={ { __html: credit } }></a>
                ) : (
                  <span className="img-credit" dangerouslySetInnerHTML={ { __html: credit } }></span>
                )}
                {(textPlacement === 'below') &&
                <>
                  {sponsored && <span className="sponsored-txt">Sponsored</span>}
                </>
                }
              </div>
            )}
          </div>

          {/* If imageUrl exists, make headline linked */}
          {isValid(imageUrl) ? (
              <a className="large-content-link" href={imageUrl} target="_blank" rel="noopener nofollow" data-link-type="text-link" >
                <SubHead className={subHeadClassName} data={headline}/>
              </a>
          ) : (
              <SubHead className={subHeadClassName} data={headline}/>
          )}

          {/* IMAGE TEXT WRAPPER: generates the category/author, header (linked), horizontal rule, body text, sponsored, and rating */}
          <div className="image-text-wrapper">
            <BodyText className="body-text" articleLayout={articleLayout} data={bodyText} isAffiliate={isAffiliate}/>
            {/* Only display rating stars if viewing on desktop and category=recipes */}
            {(!isMobile && isRecipe && hasRating) && 
              <StarRating id={articleId} slug={slug} average={avgRating} isMobile={isMobile} header={false} />
            }
            {/* NOT USING - Editors will add buttons in body-text module in WP editor - Button for large-content-container */}
            <div className="large-content-btn-wrapper btn-below standard">
              {(isText(containerClassName) && containerClassName.includes('large-content-container below'))
                &&
                <div className={isStandard ? 'button-wrapper standard' : 'button-wrapper'}>
                  {renderedButton}
                </div>
              }
            </div>
          </div>
        </>
      }
      
    </div>
  );
};

ContentModule.propTypes = {
  data: PropTypes.object,
  className: PropTypes.string,
  isMobile: PropTypes.bool,
};

export default ContentModule;
